import React, { useEffect, useState } from 'react';
import {
    WallSaveState,
    SharedRecommendedDesign,
    SharedWall,
    sameIdSet
} from '../../../services';
import { DesignPreviewModal } from './DesignPreviewModal.component';
import { ShareDesignCard } from './ShareDesignCard.component';

// #region ShareWallSectionProps
// Save state is passed in (not read from jotai) so the dashboard branding
// preview can render this exact component against sample data.
export interface ShareWallSectionProps {
    wall: SharedWall;
    picks: number[];
    locked: boolean;
    saveState: WallSaveState;
    lastSavedPicks: number[];
    onToggle: (wallId: number, designId: number) => void;
    onSave: (wallId: number, designIds: number[]) => void;
    // Silent refetch so the preview modal can poll for a generating mockup.
    onRefresh?: () => void;
}

// #region wallIsUnfulfillable
// A wall the client can't pick for — no recommended designs. Render safely.
export const wallIsUnfulfillable = (wall: SharedWall): boolean =>
    wall.recommended_designs.length === 0;

// #region SaveStateBadge
const SaveStateBadge: React.FunctionComponent<{ state: WallSaveState; onRetry: () => void }> = (props): JSX.Element | null => {
    const { state, onRetry } = props;
    if (state === 'saving') {
        return <span className="text-xs text-share-muted">Saving...</span>;
    }
    if (state === 'saved') {
        return <span className="text-xs font-medium text-emerald-600">Saved ✓</span>;
    }
    if (state === 'error') {
        return (
            <span className="text-xs text-red-600">
                Save failed —{' '}
                <button type="button" className="font-semibold underline" onClick={onRetry}>
                    Retry
                </button>
            </span>
        );
    }
    return null;
};

// #region ShareWallSection
// One wall: name + instruction, room image, design grid, auto-save.
// One design is chosen per wall.
export const ShareWallSection: React.FunctionComponent<ShareWallSectionProps> = (props: ShareWallSectionProps): JSX.Element => {
    const { wall, picks, locked, saveState, lastSavedPicks, onToggle, onSave, onRefresh } = props;

    const [preview, setPreview] = useState<SharedRecommendedDesign | null>(null);
    const [photoBroken, setPhotoBroken] = useState(false);

    const hasPick = picks.length === 1;
    const unfulfillable = wallIsUnfulfillable(wall);

    // Auto-save when the wall has its one pick and it differs from the last saved.
    useEffect(() => {
        if (locked || unfulfillable) return;
        if (picks.length !== 1) return;
        if (sameIdSet(picks, lastSavedPicks)) return;
        onSave(wall.id, picks);
    }, [picks, locked]);

    const specs: string[] = [];
    if (wall.wall_width != null && wall.wall_height != null) {
        specs.push(`${wall.wall_width}" × ${wall.wall_height}"`);
    }

    // Once the client picks a design (and its mockup is ready), show that design
    // ON the wall (its AI mockup) as the room picture instead of the plain photo.
    const pickedRec = picks.length === 1
        ? wall.recommended_designs.find((design) => design.design_id === picks[0])
        : undefined;
    const showingMockup = pickedRec?.mockup_status === 'GENERATED' && !!pickedRec.ai_mockup_signed_url;
    const roomImageUrl = showingMockup ? pickedRec?.ai_mockup_signed_url : wall.wall_photo_signed_url;

    // Reset the broken flag when the displayed image switches (photo ⇄ mockup).
    useEffect(() => { setPhotoBroken(false); }, [roomImageUrl]);

    return (
        // No `overflow-hidden` here — it would break the sticky room image.
        // Rounded corners are applied to the first/last children instead.
        <section id={`wall-${wall.id}`} className={`border border-share-border bg-share-raised shadow-sm ${roomImageUrl && !photoBroken ? 'rounded-b-3xl' : 'rounded-3xl'}`}>
            {roomImageUrl && !photoBroken && (
                // Pinned to the top of the scroll container while the client
                // scrolls this wall's designs, released when the next wall's
                // section pushes it off. The 8px offset is white padding inside
                // the sticky element rather than a `top` offset, so nothing is
                // ever visible scrolling through the gap.
                <div className="sticky top-0 z-20 overflow-hidden bg-white pt-2 shadow-[0_6px_10px_-6px_rgba(0,0,0,0.28)]">
                    <img
                        key={roomImageUrl}
                        src={roomImageUrl}
                        alt={showingMockup ? `${wall.name} with your selected design` : `${wall.name} wall`}
                        loading="lazy"
                        className="h-44 w-full object-cover object-center sm:h-72"
                        onError={() => setPhotoBroken(true)}
                    />
                    {showingMockup && (
                        <span
                            className="absolute left-3 top-5 rounded-full px-2.5 py-1 text-[11px] font-semibold shadow"
                            style={{ backgroundColor: 'var(--share-primary)', color: 'var(--share-primary-contrast)' }}
                        >
                            Your pick, on your wall
                        </span>
                    )}
                </div>
            )}

            <div className={`relative z-0 rounded-b-3xl bg-share-raised p-4 sm:p-6 ${roomImageUrl && !photoBroken ? '' : 'rounded-t-3xl'}`}>
                <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                    <div className="min-w-0">
                        <h2 className="text-lg font-semibold tracking-tight text-share-text sm:text-xl">{wall.name}</h2>
                        {specs.length > 0 && (
                            <div className="text-xs text-share-muted">{specs.join(' · ')}</div>
                        )}
                    </div>
                    {!unfulfillable && (
                        <div className="flex items-center gap-3">
                            <span className={`text-sm font-medium ${hasPick ? 'text-emerald-600' : 'text-share-muted'}`}>
                                {hasPick ? 'Selected' : 'Pick one design'}
                            </span>
                            {!locked && (
                                <SaveStateBadge
                                    state={saveState}
                                    onRetry={() => { if (hasPick) onSave(wall.id, picks); }}
                                />
                            )}
                        </div>
                    )}
                </div>

                {unfulfillable ? (
                    <div className="mt-4 rounded-2xl border border-dashed border-share-border py-10 text-center text-sm text-share-muted">
                        No options available for this wall.
                    </div>
                ) : (
                    // One card per row on phones; two from `sm`, three from `lg`.
                    <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-3">
                        {wall.recommended_designs.map((design: SharedRecommendedDesign) => (
                            <ShareDesignCard
                                key={design.design_id}
                                design={design}
                                selected={picks.includes(design.design_id)}
                                atCapacity={false}
                                locked={locked}
                                onToggle={(designId: number) => onToggle(wall.id, designId)}
                                onPreview={setPreview}
                            />
                        ))}
                    </div>
                )}
            </div>

            <DesignPreviewModal
                designs={wall.recommended_designs}
                initialDesignId={preview?.design_id ?? null}
                open={preview !== null}
                picks={picks}
                locked={locked}
                onToggle={(designId: number) => onToggle(wall.id, designId)}
                onClose={() => setPreview(null)}
                onRefresh={onRefresh}
            />
        </section>
    );
};
